import { FeatureVisibility } from '@prisma/client';
import {
  softDelete,
  stringToEnum,
  timestamps,
  uuid,
} from '@snap-types/models/common';
import {
  leadTimeFormatSchema,
  partSpecTableSchema,
  snapQuoteWonLossStatusSchema,
} from '@snap-types/models/enums';
import {
  DisplayMagnitude,
  displayMagnitudeSchema,
} from '@snap-types/models/quoting-config/enums';
import { schema } from '@snap-types/schemas';
import { decimal } from '@snap-types/schemas/decimal';
import { dec } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

const featureVisibilitySchema = stringToEnum(FeatureVisibility);

export const columns = {
  uuid,
  defaultPartRequirementFixedCost: z.number().int().nullish(),
  quantityDisplayMagnitude: displayMagnitudeSchema,
  decimalPlaces: z.number(),
  daysUntilExpiration: z.number(),
  daysUntilExpectedWin: z.number(),
  defaultWonLossStatus: snapQuoteWonLossStatusSchema,
  partSpecTable: partSpecTableSchema,
  leadTimeFormat: leadTimeFormatSchema,
  partInsightsMaxDaysForInclusion: z.number(),
  partInsightsMaxDaysForRecent: z.number(),
  partInsightsHighWinRate: decimal,
  partInsightsLeadTimeRangeLow: decimal,
  partInsightsLeadTimeRangeHigh: decimal,
  partInsightsLeadTimeMaxDaysForInclusion: z.number(),

  // Feature Visibility
  partInsightsVisible: featureVisibilitySchema,
  duplicateQuoteVisible: featureVisibilitySchema,

  createdByUserUuid: z.uuid().nullish(),
  ...timestamps,
  ...softDelete,
} as const;

const d = schema.t.dec;

export const columnValidators = {
  defaultPartRequirementFixedCost: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .nullish()
    .or(z.literal('').transform(() => null)),
  quantityDisplayMagnitude: displayMagnitudeSchema.default(
    DisplayMagnitude.ONES,
  ),
  decimalPlaces: schema.t.num.fromString
    .pipe(
      schema.t.int.positive
        .min(1, { error: 'Must be 1 or more' })
        .max(10, { error: 'Must be 10 or less' }),
    )
    .default(2),
  daysUntilExpiration: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(30),
  daysUntilExpectedWin: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(2),
  defaultWonLossStatus: snapQuoteWonLossStatusSchema,
  partSpecTable: partSpecTableSchema,
  leadTimeFormat: leadTimeFormatSchema,
  partInsightsMaxDaysForInclusion: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(1095),
  partInsightsMaxDaysForRecent: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(365),
  partInsightsHighWinRate: d.fromDecimalLike
    .default(dec(0.7))
    .check(d.checks.gte(0))
    .check(d.checks.lte(1)),
  partInsightsVisible: featureVisibilitySchema.default(FeatureVisibility.ALL),
  partInsightsLeadTimeRangeLow: d.fromDecimalLike.default(dec(0.1)),
  partInsightsLeadTimeRangeHigh: d.fromDecimalLike.default(dec(0.3)),
  partInsightsLeadTimeMaxDaysForInclusion: schema.t.num.fromString
    .pipe(schema.t.int.positive)
    .default(730),
  duplicateQuoteVisible: featureVisibilitySchema.default(FeatureVisibility.ALL),
} as const;
